// -----------------------------------------------------------------
// Bulk Capture auth storage — single source of truth for the SSO
// token and the operator's selected outlet.
//
// Migrated 2026-02 from sessionStorage → localStorage so that:
//   • closing/reopening a tab does not force re-SSO
//   • opening a new tab inherits the session
//   • mobile Safari / memory-saver tab discard does not destroy auth
//
// Migration is one-way and idempotent: on first read after upgrade,
// any value still in sessionStorage is copied to localStorage and the
// sessionStorage key is cleared. Downgrade (localStorage → session)
// is intentionally NOT supported.
// -----------------------------------------------------------------

export const TOKEN_KEY  = "bulk_capture_sso_token";
export const OUTLET_KEY = "bulk_capture_outlet_id";
export const STORAGE_BACKEND = "localStorage"; // Reported in telemetry.

function _readOnce(key) {
  // Prefer localStorage; if missing, look in sessionStorage and
  // upgrade in-place. Any read exception (Safari private mode, etc.)
  // returns null — callers treat "no token" as "not authenticated".
  try {
    const ls = window.localStorage.getItem(key);
    if (ls != null) return ls;
  } catch { /* localStorage unavailable */ }
  try {
    const ss = window.sessionStorage.getItem(key);
    if (ss != null) {
      // One-shot migration.
      try { window.localStorage.setItem(key, ss); } catch { /* ignore */ }
      try { window.sessionStorage.removeItem(key); } catch { /* ignore */ }
      return ss;
    }
  } catch { /* sessionStorage unavailable */ }
  return null;
}

export function getStoredToken()   { return _readOnce(TOKEN_KEY); }
export function getStoredOutlet()  { return _readOnce(OUTLET_KEY); }

export function setStoredToken(token) {
  if (typeof token !== "string" || token.trim().length < 20) return;
  const clean = token.trim();
  try { window.localStorage.setItem(TOKEN_KEY, clean); } catch { /* ignore */ }
  // Belt-and-braces: purge any stale sessionStorage copy so both
  // storages don't disagree after login.
  try { window.sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

export function setStoredOutlet(id) {
  if (typeof id !== "string" || !id) return;
  try { window.localStorage.setItem(OUTLET_KEY, id); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(OUTLET_KEY); } catch { /* ignore */ }
}

export function clearStoredToken() {
  try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

export function clearStoredOutlet() {
  try { window.localStorage.removeItem(OUTLET_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(OUTLET_KEY); } catch { /* ignore */ }
}

/**
 * Subscribe to cross-tab logout / login events.
 *
 * Fired when another tab calls `clearStoredToken()` or
 * `setStoredToken()`. The callback receives the new value (may be
 * null) so the SessionContext can wipe its in-memory user without
 * blindly forcing a redirect.
 */
export function onTokenStorageChange(cb) {
  const handler = (e) => {
    if (e.storageArea !== window.localStorage) return;
    if (e.key !== TOKEN_KEY) return;
    cb(e.newValue);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
